import { ActionButton, Buttons, CancelButton, Drawer, Icon, SingleSelect, Table, Tooltip } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import Fuse from 'fuse.js';
import _ from 'lodash';

export default function TaxRateMappings({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);
  const { xeroConnectionId } = useParams();

  const [{ taxRates, xeroTaxRates, isReady }, setState] = useState({ taxRates: null, isReady: false });

  useEffect(() => {
    (async () => {
      const { data: taxRates } = await api.www.workspaces(workspace.id).xero(xeroConnectionId).getTaxRates();

      const { data: xeroTaxRates } = await api.www.workspaces(workspace.id).xero(xeroConnectionId).getXeroTaxRates();

      if (!isMounted.current) return;
      setState({ taxRates, xeroTaxRates, isReady: true });
    })();
  }, [api, workspace.id, isMounted, xeroConnectionId]);

  const updateTaxRateMapping = ({ taxRateId, xeroTaxType }) => {
    setDirty(true);

    setState((state) => ({
      ...state,
      taxRates: taxRates.map((i) => (i.id === taxRateId ? { ...i, xeroTaxType } : i)),
    }));
  };

  return (
    <Drawer
      isOpen
      title="Tax Rate Mappings"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();

          try {
            const mappings = taxRates.map((taxRate) => ({
              taxRateId: taxRate.id,
              xeroTaxType: taxRate.xeroTaxType,
            }));
            await api.www.workspaces(workspace.id).xero(xeroConnectionId).updateTaxRateMappings(mappings);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        if (!isReady) return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="40%">Tax Rate</Table.Column>

                <Table.Column>
                  <strong>Xero Tax Rate</strong>
                </Table.Column>
              </Table.BoxHeader>

              <Table.Body>
                {taxRates.map((taxRate) => {
                  const handleChange = (event) => {
                    updateTaxRateMapping({
                      taxRateId: taxRate.id,
                      xeroTaxType: event.target.value || null,
                    });
                  };

                  return (
                    <Table.BoxRow key={taxRate.id}>
                      <Table.Cell>{taxRate.name}</Table.Cell>
                      <Table.Cell>
                        <TaxRateSelect taxRates={xeroTaxRates} value={taxRate.xeroTaxType} onChange={handleChange} />
                      </Table.Cell>
                    </Table.BoxRow>
                  );
                })}
              </Table.Body>
            </Table>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}

function TaxRateSelect({ taxRates, value, ...props }) {
  const [filterValue, setFilterValue] = useState('');

  const options = useMemo(() => {
    if (!filterValue) {
      return taxRates;
    }

    const fuse = new Fuse(taxRates, { keys: ['name'], threshold: 0.1 });
    const results = fuse.search(filterValue);
    return results.map((result) => result.item);
  }, [taxRates, filterValue]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const client = _.find(taxRates, { id: value });
    if (!client)
      return {
        id: value,
        name: (
          <Tooltip message="Tax Rate not found in Xero">
            <Icon icon="warning" color={colors.warning} spaceRight />
            Tax Rate ID: {value}
          </Tooltip>
        ),
      };
    return client;
  }, [taxRates, value]);

  return (
    <SingleSelect
      showEmptyOption
      showFilter
      value={value}
      valueRenderer={selectedOption?.name ?? ''}
      onFilterChange={(event) => setFilterValue(event.target.value)}
      {...props}>
      {options.map((option) => (
        // eslint-disable-next-line react/no-unknown-property
        <option key={option.id} value={option.id} tooltip={option.name}>
          {option.name}
        </option>
      ))}
    </SingleSelect>
  );
}
